import * as THREE from 'three';

// Both victory scenes use the same sky, with the liberation scene revealing it
// through the expanding wave. Each galaxy rotates within its inclined disc.
export const createVictoryGalaxy = ({
  dieColor = '#a9ffed',
  cinematic = false,
  reduceMotion = false,
} = {}) => {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      aspect: { value: 1 },
      breath: { value: 1 },
      tear: { value: 1 },
      cinematic: { value: cinematic ? 1 : 0 },
      still: { value: reduceMotion ? 1 : 0 },
      dieColor: { value: new THREE.Color(dieColor) },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.9999, 1.0);
      }
    `,
    fragmentShader: `
      varying vec2 vUv;
      uniform float time;
      uniform float aspect;
      uniform float breath;
      uniform float tear;
      uniform float cinematic;
      uniform float still;
      uniform vec3 dieColor;

      mat2 rotate(float angle) {
        float c = cos(angle);
        float s = sin(angle);
        return mat2(c, -s, s, c);
      }
      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
          mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0)), f.x), f.y);
      }
      float cloud(vec2 p) {
        float value = 0.0;
        float weight = 0.5;
        for (int octave = 0; octave < 3; octave++) {
          value += noise(p) * weight;
          p = mat2(1.6, -1.2, 1.2, 1.6) * p + 3.7;
          weight *= 0.5;
        }
        return value;
      }
      float stars(vec2 p, float density) {
        vec2 cell = floor(p * density);
        vec2 local = fract(p * density) - 0.5;
        float seed = hash(cell);
        vec2 offset = vec2(hash(cell + 9.3), hash(cell - 7.1)) * 0.65 - 0.325;
        float point = exp(-dot(local - offset, local - offset) * 650.0);
        float twinkle = 0.78 + 0.22 * sin(time * 0.65 + seed * 40.0);
        return point * step(0.96, seed) * twinkle;
      }
      vec3 galaxy(vec2 p, vec2 center, float size, float tilt,
        float spin, float phase, vec3 tint) {
        vec2 disc = rotate(tilt) * (p - center) / size;
        disc.y *= 1.65;
        float radius = length(disc);
        if (radius > 1.9) return vec3(0.0);
        vec2 orbit = rotate(time * spin + phase) * disc;
        float angle = atan(orbit.y, orbit.x + 0.0001);
        float dust = cloud(orbit * 3.6 + phase * 7.0);
        float grain = noise(orbit * 35.0 + phase);
        float arms = pow(0.5 + 0.5 * sin(angle * 2.0 - radius * 8.0
          + dust * 1.5), 3.0);
        float envelope = exp(-radius * 1.8)
          * (1.0 - smoothstep(1.1, 1.9, radius));
        float filaments = (0.12 + arms * 1.25) * (0.4 + dust * 0.7)
          * (0.65 + grain * 0.35) * envelope;
        vec3 blue = vec3(0.12, 0.58, 0.85);
        vec3 color = mix(tint, blue, dust * 0.8) * filaments * 1.5;
        color += vec3(0.7, 0.86, 1.0) * stars(orbit, 28.0)
          * envelope * (0.15 + arms * 0.85);
        color += vec3(0.85, 0.52, 0.32) * exp(-radius * 10.0) * 0.75;
        color += vec3(1.0, 0.93, 0.8) * exp(-radius * 38.0) * 1.1;
        return color * (0.94 + 0.06 * sin(time * 0.2 + phase));
      }
      void main() {
        vec2 p = (vUv - 0.5) * vec2(aspect, 1.0);
        float span = clamp(aspect, 0.65, 1.5);
        vec3 sky = vec3(0.006, 0.012, 0.035);
        float haze = cloud(p * 2.5 + vec2(time * 0.004, 0.0));
        sky += mix(vec3(0.025, 0.008, 0.07), vec3(0.008, 0.06, 0.08), haze)
          * (0.3 + haze * 0.7);
        sky += galaxy(p, vec2(-aspect * 0.27, 0.1), span * 0.38,
          -0.38, 0.035, 0.0, vec3(0.42, 0.15, 0.72))
          * mix(smoothstep(0.15, 2.6, time), 1.0, still);
        sky += galaxy(p, vec2(aspect * 0.29, -0.07), span * 0.3,
          0.48, -0.028, 2.4, vec3(0.18, 0.4, 0.8))
          * mix(smoothstep(0.8, 3.6, time), 1.0, still);
        sky += galaxy(p, vec2(aspect * 0.05, 0.33), span * 0.13,
          -0.22, 0.022, 4.1, vec3(0.55, 0.24, 0.64))
          * mix(smoothstep(1.4, 4.4, time), 1.0, still) * 0.65;
        sky += vec3(0.55, 0.72, 1.0)
          * (stars(p + time * 0.0006, 65.0) + stars(p, 125.0) * 0.45);

        float distanceFromDie = length(p);
        float reach = breath * (length(vec2(aspect, 1.0)) * 0.65 + 0.1);
        float cleared = 1.0 - smoothstep(reach - 0.06, reach + 0.12, distanceFromDie);
        float wave = exp(-pow((distanceFromDie - reach) * 55.0, 2.0))
          * smoothstep(0.0, 0.12, breath) * (1.0 - smoothstep(0.75, 1.0, breath));
        vec3 color = mix(vec3(0.001, 0.002, 0.006), sky, max(cleared * 0.7, tear));
        color += mix(dieColor, vec3(0.8, 1.0, 0.97), 0.65) * wave * 0.6;
        color *= 1.0 - smoothstep(0.35, 1.1, length(vUv - 0.5)) * 0.5;

        // Keep the champion's existing light and halo layers visible beneath
        // the sky. The cinematic uses an opaque sky for its darkness reveal.
        float alpha = mix(clamp(max(max(color.r, color.g), color.b), 0.001, 1.0),
          1.0, cinematic);
        gl_FragColor = vec4(color / alpha, alpha);
      }
    `,
    transparent: !cinematic,
    // The transparent sky must also stay behind the champion's opaque body.
    depthTest: true,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  mesh.frustumCulled = false;
  mesh.renderOrder = -10;

  return {
    mesh,
    resize(aspect) {
      material.uniforms.aspect.value = aspect;
    },
    update(elapsed, { breath = 1, tear = 1 } = {}) {
      material.uniforms.time.value = reduceMotion ? 0 : elapsed;
      material.uniforms.breath.value = breath;
      material.uniforms.tear.value = tear;
    },
  };
};
